import {Component, forwardRef, Injectable, Input} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import {NgbCalendar, NgbDateAdapter, NgbDateParserFormatter, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {UntilDestroy} from "@ngneat/until-destroy";
import {IoniconsOutline} from "@pt/pt-models";

@Injectable()
export class CustomAdapter extends NgbDateAdapter<string> {
  readonly DELIMITER = '-';

  fromModel(value: string | null): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.DELIMITER);
      return {
        day: parseInt(date[0], 10),
        month: parseInt(date[1], 10),
        year: parseInt(date[2], 10),
      };
    }
    return null;
  }

  toModel(date: NgbDateStruct | null): string | null {
    return date ? this.adjust(date.day) + this.DELIMITER + this.adjust(date.month) + this.DELIMITER + date.year : null;
  }

  private adjust(number): string {
    if (number > 9) {
      return number.toString()
    }
    return '0' + number;
  }

}

@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = '/';

  parse(value: string): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.DELIMITER);
      return {
        day: parseInt(date[0], 10),
        month: parseInt(date[1], 10),
        year: parseInt(date[2], 10),
      };
    }
    return null;
  }

  format(date: NgbDateStruct | null): string {
    return date ? this.adjust(date.day) + this.DELIMITER + this.adjust(date.month) + this.DELIMITER + date.year : '';
  }

  private adjust(number): string {
    if (number > 9) {
      return number.toString()
    }
    return '0' + number;
  }

}

@UntilDestroy()
@Component({
  selector: 'pt-input-date-picker',
  // NOTE: For this example we are only providing current component, but probably
  // NOTE: you will want to provide your main App Module
  providers: [
    {provide: NgbDateAdapter, useClass: CustomAdapter},
    {provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter},
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true, useExisting: forwardRef(() => InputDatePickerComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputDatePickerComponent),
      multi: true
    }
  ],
  template: `
    <form class="row row-cols-sm-auto overflow-hidden" style="width: max-content;">
      <div class="col-12">
        <pt-input-label
          style='display: contents'
          *ngIf='label'
          [label]='label'
          [isRequired]='isRequired'
        ></pt-input-label>

        <div class="input-group">
          <input
            class="form-control input-calendar is-invalid"
            [ngClass]="{'is-invalid': !isValid}"
            placeholder="dd/mm/yyyy"
            [disabled]="isDisabled"
            container="body"
            name="d2"
            [(ngModel)]="models"
            (ngModelChange)="changeValue($event)"
            ngbDatepicker
            #d2="ngbDatepicker"
          />
          <button class="btn btn-outline-secondary btn-calendar"
                  [disabled]="isDisabled"
                  (click)="d2.toggle()" type="button">
            <pt-icon class="d-flex" [key]='IconCalendar' [size]='16'></pt-icon>
          </button>
        </div>
      </div>
    </form>

  `,
  styles: [
    `
      .label-calendar {

      }

      .input-calendar {
        height: 25px;
        border-radius: 0;
        font-size: 0.875rem;
      }

      .btn-calendar {
        padding: 0 8px;
        height: 25px;
        border-radius: 0;
        display: flex;
        align-items: center;
        background: #e9ecef;
      }
    `
  ],
})
export class InputDatePickerComponent implements ControlValueAccessor {
  @Input() label: string;
  @Input() isRequired = true;

  constructor(
    private ngbCalendar: NgbCalendar,
    private dateAdapter: NgbDateAdapter<string>,
  ) {}

  public models?: string;
  public IconCalendar = IoniconsOutline.calendarCheck;
  private dateDDMMYYYYRegex = new RegExp(/^((\d{2})-(\d{2})-(\d{4}))$/);
  private onChanged: (val) => void;
  private onTouched: () => void;
  private latestValue?: number;
  public isDisabled = false;
  public isValid = true;

  changeValue(value: string) {
    if (this.dateDDMMYYYYRegex.test(value)) {
      this.latestValue = new Date(value).getTime();

      if (this.onChanged) {
        this.onChanged(this.latestValue);
      }
      if (this.onTouched) {
        this.onTouched();
      }
      this.isValid = true;
    } else {
      if (this.latestValue !== null) {
        if (this.onChanged) {
          this.onChanged(null);
        }
        if (this.onTouched) {
          this.onTouched();
        }
      } else {
      }
      this.latestValue = null;
      this.isValid = !this.isRequired
    }
  }

  writeValue(inputDate: number): void {
    if (inputDate) {
      const date = new Date(inputDate);
      const ngbDate = {
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear()
      }
      this.models = this.dateAdapter.toModel(ngbDate);
    } else {
      this.models = this.dateAdapter.toModel(undefined);
    }
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.isRequired) {
      return this.latestValue > 0 ? null : {invalidDate: true};
    }
    return null;

  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }


}

